// First-run onboarding: the guided TOUR_STEPS + OnboardingTour overlay, the
// full-screen Walkthrough, and the email-verify banner.
import { useState, useEffect, useRef } from "react";
import { sendEmailVerification } from "firebase/auth";
import { LS } from "../lib/firebase";
import { PrabzoLogo, Icon } from "../ui/primitives";

export const TOUR_STEPS=[
  {
    icon:"dashboard",
    title:"Welcome to Prabzo! 🎉",
    desc:"Your all-in-one client manager. Dashboard gives you a live snapshot — active clients, payments due, today's sessions, and your revenue at a glance.",
    color:"#0f766e",bg:"#ccfbf1",
  },
  {
    icon:"clients",
    title:"Manage Your Clients",
    desc:"Add clients with their package, sessions, and contact details. Track remaining sessions, payment balance, and full history — all in one card.",
    color:"#0f766e",bg:"#ccfbf1",
  },
  {
    icon:"attendance",
    title:"Track Every Session",
    desc:"Mark attendance with one tap. Schedule recurring classes, book one-off appointments, and manage group batches. Notes are saved automatically.",
    color:"#1d4ed8",bg:"#dbeafe",
  },
  {
    icon:"payments",
    title:"Collect Payments",
    desc:"Record payments instantly. Generate professional receipts or invoices and share via WhatsApp. See exactly who owes what at a glance.",
    color:"#c8902a",bg:"#fef3c7",
  },
  {
    icon:"reports",
    title:"Insights & Reports",
    desc:"Track your revenue trends, top clients, attendance rates, and payment collection — all in the Reports tab. Know your business inside out.",
    color:"#7c3aed",bg:"#ede9fe",
  },
  {
    icon:"user",
    title:"Your Profile",
    desc:"Set your business name, services, and package templates. Customize client labels, currency, and theme to match your brand perfectly.",
    color:"#db2777",bg:"#fce7f3",
  },
  {
    icon:"dashboard",
    title:"Meet Shri 🪷",
    desc:"Your AI assistant! Tap the 🪷 button anytime to add clients, mark attendance, check payments, or ask anything — using voice or text.",
    color:"#0f766e",bg:"#ccfbf1",
  },
];

export const OnboardingTour=({theme,onDone})=>{
  const [step,setStep]=useState(0);
  const [exiting,setExiting]=useState(false);
  const current=TOUR_STEPS[step];
  const isLast=step===TOUR_STEPS.length-1;

  const next=()=>{
    if(isLast){setExiting(true);setTimeout(onDone,300);}
    else setStep(s=>s+1);
  };

  return(
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-4"
      style={{background:"rgba(0,0,0,0.6)",backdropFilter:"blur(4px)"}}>
      <div className="w-full max-w-sm bg-white rounded-3xl overflow-hidden shadow-2xl"
        style={{transform:exiting?"translateY(100px)":"translateY(0)",opacity:exiting?0:1,transition:"all 0.3s ease"}}>

        {/* Coloured top strip */}
        <div className="p-8 text-center" style={{background:`linear-gradient(135deg,${current.color}22,${current.bg})`}}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{background:current.color}}>
            <Icon name={current.icon} size={28} style={{color:"#fff"}}/>
          </div>
          <h2 className="text-xl font-black text-gray-900 mb-2" style={{fontFamily:"'Playfair Display',serif"}}>{current.title}</h2>
          <p className="text-sm text-gray-500 leading-relaxed">{current.desc}</p>
        </div>

        {/* Progress dots + buttons */}
        <div className="px-6 py-5">
          <div className="flex items-center justify-center gap-2 mb-5">
            {TOUR_STEPS.map((_,i)=>(
              <div key={i} className="rounded-full transition-all"
                style={{width:i===step?24:8,height:8,background:i===step?(theme?.primary||"#0f766e"):"#e5e7eb"}}/>
            ))}
          </div>
          <button onClick={next}
            className="w-full py-3 rounded-xl text-sm font-bold text-white mb-3 transition-all"
            style={{background:theme?.primary||"#0f766e"}}>
            {isLast?"Let's Go! 🚀":"Next →"}
          </button>
          <button onClick={()=>{setExiting(true);setTimeout(onDone,300);}}
            className="w-full py-2 text-sm text-gray-400 font-medium">
            Skip tour
          </button>
        </div>
      </div>
    </div>
  );
};

export const Walkthrough=({onDone})=>{
  const [slide,setSlide]=useState(0);
  const [exiting,setExiting]=useState(false);
  const timerRef=useRef(null);
  const startX=useRef(null);

  const slides=[
    {
      emoji:"📋",
      title:"Still juggling WhatsApp & Excel?",
      desc:"Most solo professionals track clients in chat threads and spreadsheets. It works — until it doesn't.",
    },
    {
      emoji:"✨",
      title:"One place for everything",
      desc:"Prabzo keeps your clients, sessions, payments, and reminders organised — so you can focus on your work, not admin.",
    },
    {
      emoji:"🧘",
      title:"Built for solo professionals",
      desc:"Yoga teachers, fitness trainers, tutors, therapists — if you have clients, Prabzo is for you.",
    },
  ];

  const goTo=(i)=>{
    clearInterval(timerRef.current);
    setSlide(i);
    if(i<slides.length-1){
      timerRef.current=setInterval(()=>{
        setSlide(s=>{
          if(s>=slides.length-1){clearInterval(timerRef.current);return s;}
          return s+1;
        });
      },3000);
    }
  };

  useEffect(()=>{
    timerRef.current=setInterval(()=>{
      setSlide(s=>{
        if(s>=slides.length-1){clearInterval(timerRef.current);return s;}
        return s+1;
      });
    },3000);
    return()=>clearInterval(timerRef.current);
  },[]);

  const finish=()=>{
    setExiting(true);
    LS.set("walkthroughDone",true);
    setTimeout(onDone,400);
  };

  const onTouchStart=(e)=>{startX.current=e.touches[0].clientX;};
  const onTouchEnd=(e)=>{
    if(startX.current===null)return;
    const diff=startX.current-e.changedTouches[0].clientX;
    if(Math.abs(diff)>50){
      if(diff>0&&slide<slides.length-1)goTo(slide+1);
      else if(diff<0&&slide>0)goTo(slide-1);
    }
    startX.current=null;
  };

  const s=slides[slide];

  return(
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6 transition-opacity duration-400"
      style={{background:"linear-gradient(135deg,#0f4c5c 0%,#0a7c8c 50%,#c8902a 100%)",opacity:exiting?0:1}}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Skip */}
      <button onClick={finish} className="absolute right-6 text-white/60 text-sm font-medium hover:text-white transition-colors"
        style={{top:`calc(env(safe-area-inset-top) + 1.5rem)`}}>
        Skip
      </button>

      {/* Slide content */}
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-sm text-center">
        {/* Logo top */}
        <div className="mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl shadow-lg mb-3" style={{background:"#0f766e",padding:"10px"}}>
            <PrabzoLogo/>
          </div>
          <p className="text-white/70 text-xs tracking-widest uppercase font-medium">Prabzo</p>
        </div>

        {/* Emoji */}
        <div key={slide} className="text-6xl mb-6" style={{animation:"fadeSlideUp 0.4s ease"}}>
          {s.emoji}
        </div>

        {/* Text */}
        <h2 key={"t"+slide} className="text-2xl font-black text-white mb-3 leading-tight" style={{fontFamily:"'Playfair Display',serif",animation:"fadeSlideUp 0.4s ease"}}>
          {s.title}
        </h2>
        <p key={"d"+slide} className="text-white/70 text-sm leading-relaxed max-w-xs" style={{animation:"fadeSlideUp 0.4s ease"}}>
          {s.desc}
        </p>
      </div>

      {/* Dots */}
      <div className="flex items-center gap-2 mb-8">
        {slides.map((_,i)=>(
          <button key={i} onClick={()=>goTo(i)}
            className="rounded-full transition-all duration-300"
            style={{
              width:i===slide?20:8,
              height:8,
              background:i===slide?"#ffffff":"rgba(255,255,255,0.35)",
            }}
          />
        ))}
      </div>

      {/* Get Started on last slide */}
      {slide===slides.length-1?(
        <button onClick={finish}
          className="w-full max-w-sm py-4 rounded-2xl text-sm font-bold text-teal-800 shadow-lg transition-all active:scale-95"
          style={{background:"#ffffff"}}>
          Get Started →
        </button>
      ):(
        <button onClick={()=>goTo(slide+1)}
          className="w-full max-w-sm py-4 rounded-2xl text-sm font-semibold transition-all active:scale-95"
          style={{background:"rgba(255,255,255,0.15)",color:"white",border:"1px solid rgba(255,255,255,0.3)"}}>
          Next
        </button>
      )}

      <style>{`
        @keyframes fadeSlideUp {
          from{opacity:0;transform:translateY(16px);}
          to{opacity:1;transform:translateY(0);}
        }
      `}</style>
    </div>
  );
};

export const EmailVerifyBanner=({user,theme,isDark})=>{
  const [sent,setSent]=useState(false);
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState("");
  const send=async()=>{
    setLoading(true);setErr("");
    try{await sendEmailVerification(user);setSent(true);}
    catch(e){setErr("Could not send: "+e.message);}
    setLoading(false);
  };
  return(
    <div className="flex items-center gap-3 px-4 py-3 rounded-2xl mb-4 text-sm"
      style={{background:isDark?"#422006":"#fff7ed",border:"1px solid #fed7aa"}}>
      <span className="text-lg">📧</span>
      <div className="flex-1">
        <p className="font-semibold text-orange-800 text-xs">Please verify your email</p>
        <p className="text-orange-600 text-xs">{sent?"Verification email sent! Check your inbox.":err||"Verify to secure your account."}</p>
      </div>
      {!sent&&<button onClick={send} disabled={loading}
        className="text-xs font-bold px-3 py-1.5 rounded-xl whitespace-nowrap"
        style={{background:theme?.primary||"#ea580c",color:"#fff"}}>
        {loading?"Sending...":"Send Link"}
      </button>}
    </div>
  );
};
